'use client';

import LazyScroll from '@/components/lazy-scroll';
import { useIsMobile } from '@/hooks/use-is-mobile';
import Image from 'next/image';
import Link from 'next/link';
import { useRef } from 'react';
import NoSsr from '../no-ssr';
import Price from '../Price';
import Description from './description';
import DesktopExploreTheWorld from './desktop-explore-the-world';
import DesktopHomeFeedback from './desktop-home-feedback';
import Faq from './faq';
import HomeImageSwitcher from './home-image-switcher';
import HomeSupportBlock from './home-support-block';
import MobileExploreTheWorld from './mobile-explore-the-world';
import MobileHomeFeedback from './mobile-home-feedback';
import MobileHomeImageSwitcher from './mobile-home-image-switcher';
import MobileSupportSlider from './mobile-support-slider';
import SpotFood from './spot-food';

const SmartBirdFeedersPage = ({ priceInfo }) => {
  const { isMobile } = useIsMobile();

  const ref = useRef(null);

  const ttbf001cpPrice = priceInfo?.TTBF001CP?.items?.[0]?.price_range?.minimum_price?.final_price;
  const ttbf002cpPrice = priceInfo?.TTBF002CP?.items?.[0]?.price_range?.minimum_price?.final_price;
  const ttbf003cpPrice = priceInfo?.TTBF003CP?.items?.[0]?.price_range?.minimum_price?.final_price;

  const gotoDetails = () => {
    ref.current?.scrollIntoView({
      behavior: 'smooth', // 平滑滚动
      block: 'start', // 元素在视窗中的垂直对齐方式
      inline: 'nearest', // 元素在视窗中的水平对齐方式
    });
  };

  return (
    <div>
      <div className='relative h-auto w-full'>
        <div className='relative flex flex-col justify-between bg-[#ccc] lg_hidden'>
          <video
            className='w-full'
            autoPlay
            loop
            muted
            poster='/images/home-banner-mobile-poster.webp'
          >
            <source
              src='/videos/home-banner-mobile.mp4'
              type='video/mp4'
            />
            Your browser does not support the video tag.
          </video>
          <div className='absolute bottom-[24px]'>
            <div className='px-[28px]'>
              <h2 className='mb-[4px] text-left text-[24px] font-[700] leading-[28px] text-white'>
                Titto time, Bounding birds prime!
              </h2>
              <h2 className='mb-[24px] text-left text-[14px] leading-[20px] text-white'>
                Bird-watching place can be any place.
              </h2>
              <div
                className='h-[44px] w-full rounded-xl bg-secondary text-center font-[700] leading-[44px] text-white'
                onClick={gotoDetails}
              >
                Order Now
              </div>
              <p className='mt-[12px] flex items-center justify-start pl-[16px] text-xs leading-[16px] text-white'>
                <img
                  className='mr-[4px] h-[16px] w-[16px]'
                  src='/icons/feeder-primary.svg'
                />
                30-day 100% money-back guarantee.
              </p>
              <p className='mt-[4px] flex items-center justify-start pl-[16px] text-xs leading-[16px] text-white'>
                <img
                  className='mr-[4px] h-[16px] w-[16px]'
                  src='/icons/shipping-primary.svg'
                />
                Shipping: United States.
              </p>
            </div>
          </div>
        </div>
        <div className='relative hidden h-auto w-full items-start justify-center lg_flex'>
          <video
            className='w-full'
            autoPlay
            loop
            muted
            poster='/images/home-banner-poster.webp'
          >
            <source
              src='/videos/home-banner.mp4'
              type='video/mp4'
            />
            Your browser does not support the video tag.
          </video>

          <div className='absolute bottom-[64px] flex flex-col items-center justify-between'>
            <h2 className='mb-[8px] text-[64px] font-[700] leading-[76px] text-white'>
              Titto time, Bounding birds prime!
            </h2>

            <h2 className='mb-[48px] text-[32px] font-[700] leading-[38px] text-white'>
              Bird-watching place can be any place.
            </h2>
            <div className='flex items-center justify-center'>
              <div
                onClick={gotoDetails}
                className='hover:border-[#FFB100] mr-[16px] h-[64px] w-[320px] cursor-pointer rounded-[24px] border-[3px] border-transparent bg-[#EE6C4D] text-center text-[32px] font-[700] leading-[64px] text-white'
              >
                Order Now!
              </div>
              <div>
                <p className='my-[6px] flex items-center justify-start leading-[22px] text-white'>
                  <img
                    className='mr-2 h-[32px] w-[32px]'
                    src='/icons/feeder-primary.svg'
                  />
                  30-day 100% money-back guarantee.
                </p>
                <p className='my-[6px] flex items-center justify-start leading-[22px] text-white'>
                  <img
                    className='mr-2 h-[32px] w-[32px]'
                    src='/icons/shipping-primary.svg'
                  />
                  Shipping: United States.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className='mb-[48px] flex flex-col items-center pt-[48px] lg_mb-[120px] lg_mt-[40px] lg_pt-0'>
          <h2 className='mb-[48px] w-[60%] text-center text-[24px] font-[700] leading-[1.2] text-title lg_mb-[32px] lg_w-full lg_text-[48px] lg_leading-[58px]'>
            Featured Highlights
          </h2>

          <div className='grid w-full max-w-full grid-cols-2 gap-[24px] px-[14px] lg_w-[1360px] lg_grid-cols-4 lg_gap-[58px] lg_px-0'>
            <div className='flex flex-col items-center justify-between p-[10px] lg_flex-row lg_justify-start lg_p-0'>
              <img
                className='mb-[20px] h-[74px] lg_mb-0 lg_mr-[24px] lg_h-auto'
                src='/icons/capability-icon-1.svg'
              />
              <div>
                <p className='text-center leading-[1.4] text-title lg_text-left'>Identify bird</p>
                <p className='text-center leading-[1.4] text-title lg_text-left'>visitors</p>
              </div>
            </div>
            <div className='flex flex-col items-center justify-between p-[10px] lg_flex-row lg_justify-start lg_p-0'>
              <img
                className='mb-[20px] h-[74px] lg_mb-0 lg_mr-[24px] lg_h-auto'
                src='/icons/capability-icon-2.svg'
              />
              <div>
                <p className='text-center leading-[1.4] text-title lg_text-left'>Send visit</p>
                <p className='text-center leading-[1.4] text-title lg_text-left'>notifications</p>
              </div>
            </div>
            <div className='flex flex-col items-center justify-between p-[10px] lg_flex-row lg_justify-start lg_p-0'>
              <img
                className='mb-[20px] h-[74px] lg_mb-0 lg_mr-[24px] lg_h-auto'
                src='/icons/capability-icon-3.svg'
              />
              <div>
                <p className='text-center leading-[1.4] text-title lg_text-left'>Display bird</p>
                <p className='text-center leading-[1.4] text-title lg_text-left'>names</p>
              </div>
            </div>
            <div className='flex flex-col items-center justify-between p-[10px] lg_flex-row lg_justify-start lg_p-0'>
              <img
                className='mb-[20px] h-[74px] lg_mb-0 lg_mr-[24px] lg_h-auto'
                src='/icons/capability-icon-4.svg'
              />
              <div>
                <p className='text-center leading-[1.4] text-title lg_text-left'>Show species</p>
                <p className='text-center leading-[1.4] text-title lg_text-left'>information</p>
              </div>
            </div>
          </div>
        </div>

        <div
          ref={ref}
          className='mx-auto flex w-[1400px] max-w-full grid-cols-2 flex-col gap-[20px] lg_flex-row lg_justify-center lg_gap-[100px]'
        >
          <Link
            href='/titto-smart-bird-feeder-pro-ttbf001cp.html'
            className='group mx-[10px] flex cursor-pointer flex-col items-center gap-[10px] rounded-[15px] bg-[#F7F7F5] p-[40px] lg_mx-0 lg_w-[450px]'
          >
            <Image
              className='w-full'
              alt=''
              width={1200}
              height={1200}
              layout='responsive'
              src='/images/smart-bird-feeders/feeder-1.jpg'
            />
            <p className='font-poppins text-[24px] font-[600] text-title'>Smart Bird Feeder Pro</p>
            <div className='flex flex-1 flex-col items-center justify-end gap-2.5'>
              <p className='text-center font-sans text-[18px] text-title'>The best choice for your backyard.</p>
              {ttbf001cpPrice !== null && ttbf001cpPrice !== undefined ? (
                <p className='font-poppins text-[24px] font-[600] leading-[1.4] text-title'>
                  <Price
                    value={ttbf001cpPrice.value}
                    currencyCode={ttbf001cpPrice.currency}
                  />
                </p>
              ) : null}

              <div className='mx-[15px] mt-[20px] block h-[40px] w-full rounded-[10px] bg-primary text-center font-poppins text-sm font-[600] uppercase leading-[40px] text-white hover_opacity-90 lg_mx-0 lg_h-[54px] lg_text-[16px] lg_leading-[54px]'>
                Shop Now
              </div>
            </div>
          </Link>
          <Link
            href='/titto-green-2-in-1-smart-bird-feeder-ttbf002cp.html'
            className='group mx-[10px] flex cursor-pointer flex-col items-center gap-[10px] rounded-[15px] bg-[#F7F7F5] p-[40px] lg_mx-0 lg_w-[450px]'
          >
            <Image
              className='w-full'
              alt=''
              width={1200}
              height={1200}
              layout='responsive'
              src='/images/smart-bird-feeders/feeder-2.jpg'
            />
            <p className='font-poppins text-[24px] font-[600] text-title'>2 in 1 Smart Bird Feeder</p>
            <div className='flex flex-1 flex-col items-center justify-end gap-2.5'>
              <p className='text-center font-sans text-[18px] text-title'>One simple buy, two ways to satisfy.</p>
              {ttbf002cpPrice !== undefined && ttbf002cpPrice !== null ? (
                <p className='font-poppins text-[24px] font-[600] leading-[1.4] text-title'>
                  <Price
                    value={ttbf002cpPrice.value}
                    currencyCode={ttbf002cpPrice.currency}
                  />
                </p>
              ) : null}

              <div className='mx-[15px] mt-[20px] block h-[40px] w-full rounded-[10px] bg-primary text-center font-poppins text-sm font-[600] uppercase leading-[40px] text-white hover_opacity-90 lg_mx-0 lg_h-[54px] lg_text-[16px] lg_leading-[54px]'>
                Shop Now
              </div>
            </div>
          </Link>
          <Link
            href='/titto-squirrel-proof-bird-feeder-with-camera-ttbf003cp.html'
            className='group mx-[10px] flex cursor-pointer flex-col items-center gap-[10px] rounded-[15px] bg-[#F7F7F5] p-[40px] lg_mx-0 lg_w-[450px]'
          >
            <Image
              className='h-auto w-full'
              alt=''
              width={1200}
              height={1200}
              src='/images/smart-bird-feeders/feeder-3.jpg'
              unoptimized
            />
            <p className='font-poppins text-[24px] font-[600] text-title'>Squirrel Proof Smart Bird Feeder</p>
            <div className='flex flex-1 flex-col items-center justify-end gap-2.5'>
              <p className='text-center font-sans text-[18px] text-title'>Feed the birds, watch them dance.</p>
              {ttbf003cpPrice !== undefined && ttbf003cpPrice !== null ? (
                <p className='font-poppins text-[24px] font-[600] leading-[1.4] text-title'>
                  <Price
                    value={ttbf003cpPrice.value}
                    currencyCode={ttbf003cpPrice.currency}
                  />
                </p>
              ) : null}

              <div className='mx-[15px] mt-[20px] block h-[40px] w-full rounded-[10px] bg-primary text-center font-poppins text-sm font-[600] uppercase leading-[40px] text-white hover_opacity-90 lg_mx-0 lg_h-[54px] lg_text-[16px] lg_leading-[54px]'>
                Shop Now
              </div>
            </div>
          </Link>
        </div>

        <NoSsr>
          <LazyScroll>
            <div className='relative mx-[14px] mb-[64px] flex max-w-full flex-col items-center overflow-hidden rounded-[24px] bg-white pb-[14px] lg_mx-auto lg_mb-[120px] lg_max-h-[720px] lg_w-[1360px] lg_rounded-[28px]'>
              <video
                width='1360'
                className='hidden w-full lg_block'
                autoPlay
                loop
                muted
              >
                <source
                  src='/videos/camera.mp4'
                  type='video/mp4'
                />
                Your browser does not support the video tag.
              </video>
              <div className='flex h-full w-full flex-col items-center pt-[48px] lg_absolute'>
                <h2 className='mb-[8px] w-[60%] text-center text-[24px] font-[700] leading-[1.2] text-title lg_mb-[16px] lg_w-full lg_text-[48px] lg_leading-[58px]'>
                  Detect, Identify, Capture, Curate.
                </h2>
                <p className='text-center text-[14px] leading-[1.4] text-[#3E3E3E] lg_text-[24px] lg_font-[700] lg_leading-[28px]'>
                  AI-powered camera.
                </p>

                <div className='flex w-full justify-center lg_hidden'>
                  <img
                    className='h-[300px] w-auto'
                    src='/images/home-camera.webp'
                  />
                </div>
              </div>
            </div>
          </LazyScroll>

          {/* Spot food, effortlessly! */}

          <LazyScroll>
            <SpotFood />
          </LazyScroll>

          <LazyScroll>{isMobile ? <MobileHomeImageSwitcher /> : <HomeImageSwitcher />}</LazyScroll>

          <LazyScroll>
            {isMobile ? (
              <div className='bg-lightGray pb-[64px]'>
                <MobileSupportSlider />
              </div>
            ) : (
              <HomeSupportBlock />
            )}
          </LazyScroll>

          <LazyScroll>
            {isMobile ? (
              <MobileExploreTheWorld onClick={gotoDetails} />
            ) : (
              <DesktopExploreTheWorld onClick={gotoDetails} />
            )}
          </LazyScroll>

          <LazyScroll>{isMobile ? <MobileHomeFeedback /> : <DesktopHomeFeedback />}</LazyScroll>

          <LazyScroll>
            <Faq />
          </LazyScroll>

          <LazyScroll>
            <Description />
          </LazyScroll>
        </NoSsr>
      </div>
    </div>
  );
};

export default SmartBirdFeedersPage;
